import React, { useState } from 'react';
import { ArrowRight, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SEOLayout from '@/components/SEOLayout';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { title: 'Advanced Coder', range: '$80k – $180k', type: 'Engineering', desc: 'Build and extend platform infrastructure. Python, React, FastAPI, MongoDB.' },
  { title: 'Automation Engineer', range: '$70k – $150k', type: 'Engineering', desc: 'Design and deploy production AI workflows. Integration experience required.' },
  { title: 'AI Workflow Architect', range: '$100k – $250k+', type: 'Engineering', desc: 'Architect complex multi-step AI systems. Blueprint design and optimization.' },
  { title: 'Digital Software Sales', range: '$60k – $200k+', type: 'Sales', desc: 'Sell AI operations to businesses. Enterprise and mid-market focus. OTE structure.' },
  { title: 'Growth Marketing', range: '$50k – $120k', type: 'Marketing', desc: 'Drive operator and business acquisition. SEO, content, paid channels.' },
];

const Careers = () => {
  const [form, setForm] = useState({ name: '', email: '', role: '', experience: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleApply = async () => {
    if (!form.name || !form.email || !form.role) return toast.error('Name, email, and role required');
    setSubmitting(true);
    try {
      await axios.post(`${API}/hub/email/send`, { to: 'info@humanscaled.ai', subject: `Career Application: ${form.role} - ${form.name}`, html: `<p><b>${form.name}</b> (${form.email})<br/>Role: ${form.role}<br/>Experience: ${form.experience}<br/>Message: ${form.message}</p>` }).catch(() => {});
      toast.success('Application submitted. We will be in touch.');
      setForm({ name: '', email: '', role: '', experience: '', message: '' });
    } catch { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <SEOLayout title="AI Automation Jobs — Engineers, Sales, Marketing | HumanScaled" description="Join HumanScaled. We're hiring automation engineers, AI workflow architects, software sales, and growth marketing. $50k-$250k+ compensation.">
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 space-y-6">
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Build the Future of<br /><span className="text-emerald-400">Controlled AI Infrastructure.</span></h1>
          <p className="text-lg text-muted-foreground max-w-2xl">We are hiring engineers, architects, sales professionals, and marketers who believe AI should be governed, not unleashed.</p>
        </div>
      </section>

      <section className="py-16 bg-[#0a0a0a]/50 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-bold mb-8">Open Positions</h2>
          <div className="space-y-4">
            {ROLES.map((role, i) => (
              <div key={i} className="bg-[#0a0a0a] border border-white/5 p-6 hover:border-emerald-500/20 transition-colors" itemScope itemType="https://schema.org/JobPosting">
                <meta itemProp="datePosted" content="2026-02-01" />
                <meta itemProp="employmentType" content="FULL_TIME" />
                <div itemProp="hiringOrganization" itemScope itemType="https://schema.org/Organization"><meta itemProp="name" content="HumanScaled" /></div>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg" itemProp="title">{role.title}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-emerald-500/10 text-emerald-400 font-mono">{role.type}</span>
                      <span className="text-xs px-2 py-0.5 bg-white/5 text-muted-foreground font-mono">Remote</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2" itemProp="description">{role.desc}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="font-mono text-emerald-400 font-bold" itemProp="baseSalary">{role.range}</p>
                    <p className="text-xs text-muted-foreground">per year</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-white/5">
        <div className="max-w-lg mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl font-bold mb-6 text-center">Apply Now</h2>
          <div className="space-y-3">
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full Name" className="bg-[#0a0a0a] border-white/10 rounded-sm" />
            <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#0a0a0a] border-white/10 rounded-sm" />
            <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-[#0a0a0a] border-white/10 rounded-sm"><SelectValue placeholder="Select Role" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10">{ROLES.map(r => <SelectItem key={r.title} value={r.title}>{r.title}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })} placeholder="Years of experience" className="bg-[#0a0a0a] border-white/10 rounded-sm" />
            <Textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Why HumanScaled?" className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[80px]" />
            <Button onClick={handleApply} disabled={submitting} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-11"><Send className="w-4 h-4 mr-2" /> Submit Application</Button>
          </div>
        </div>
      </section>
    </SEOLayout>
  );
};

export default Careers;
